import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Helper to get user's timezone (IANA format like "Asia/Kolkata")
const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'UTC'; // Fallback to UTC if browser doesn't support
  }
};

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't auto-logout for UPI and payment related endpoints
      // These will be handled by the calling code
      const url = error.config?.url || '';
      const skipAutoLogout = url.includes('/upi/') || url.includes('/payment');
      
      if (!skipAutoLogout) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  sendOTP: (phone) => api.post('/auth/otp/send', { phone }),
  verifyOTP: (phone, otp) => api.post('/auth/otp/verify', { phone, otp }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Gold Rate API
export const goldRateAPI = {
  getRate: () => api.get('/gold-rate'),
};

// Referral API
export const referralAPI = {
  getCode: () => api.get('/referral/code'),
  applyCode: (referral_code) => api.post('/referral/apply', { referral_code }),
  getStats: () => api.get('/referral/stats'),
};

// PAN KYC API
export const panAPI = {
  // User endpoints
  submit: (pan_number, pan_photo) => api.post('/kyc/pan/submit', { pan_number, pan_photo }),
  getStatus: () => api.get('/kyc/pan/status'),
  getPhoto: () => api.get('/kyc/pan/photo'),
  delete: () => api.delete('/kyc/pan'),
};

// Aadhaar KYC API
export const aadhaarAPI = {
  // Verify QR code data - returns decoded info for confirmation
  verifyQR: (qr_data, verify_mobile = false) => api.post('/kyc/aadhaar/verify-qr', { qr_data, verify_mobile }),
  // Confirm and link Aadhaar after user reviews data
  confirm: (qr_data, aadhaar_number) => api.post('/kyc/aadhaar/confirm', { qr_data, aadhaar_number }),
  // Get current Aadhaar status
  getStatus: () => api.get('/kyc/aadhaar/status'),
};

// Transaction APIs
export const transactionAPI = {
  buy: (amount_inr, payment_method = 'razorpay', custom_order_id = null) => {
    const payload = { amount_inr, payment_method };
    if (custom_order_id) {
      payload.custom_order_id = custom_order_id;
    }
    return api.post('/milli/buy', payload);
  },
  verifyPayment: (data) => api.post('/payments/razorpay/verify', data),
  checkHdfcStatus: (transactionId) => api.get(`/payments/hdfc/status/${transactionId}`),
  submitHdfcCallback: (data) => api.post('/payments/hdfc/client-callback', data),
  abortTransaction: (transactionId) => api.post(`/transactions/${transactionId}/abort`),
  sell: (milli_amount) => api.post('/milli/sell', { milli_amount }),
  redeem: (milli_amount) => api.post('/milli/redeem', { milli_amount }),
  getHistory: (limit = 50) => api.get(`/transactions?limit=${limit}`),
  getVouchers: () => api.get('/vouchers'),
};

// Payment Methods API
export const paymentAPI = {
  getMethods: () => api.get('/payment-methods'),
};

// UPI Gateway API
export const upiAPI = {
  initiate: async (amount_inr, milli_amount, rate_at_time) => {
    try {
      return await api.post('/upi/initiate', { amount_inr, milli_amount, rate_at_time });
    } catch (error) {
      // Don't let 401 errors cause automatic logout for UPI calls
      // The BuyPage will handle the error appropriately
      if (error.response?.status === 401) {
        throw new Error('Please login again to continue');
      }
      throw error;
    }
  },
  checkStatus: async (orderId) => {
    try {
      return await api.get(`/upi/status/${orderId}`);
    } catch (error) {
      // For status checks, don't trigger logout
      if (error.response?.status === 401) {
        return { data: { status: 'pending', message: 'Session check failed' } };
      }
      throw error;
    }
  },
  getQRCode: (orderId) => `${BACKEND_URL}/api/upi/qr/${orderId}`,
};

// Passbook APIs
export const passbookAPI = {
  get: (startDate, endDate, page = 1, limit = 20, transactionType = null) => {
    let url = '/passbook';
    const params = [];
    params.push(`page=${page}`);
    params.push(`limit=${limit}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (transactionType && transactionType !== 'all') params.push(`transaction_type=${transactionType}`);
    url += `?${params.join('&')}`;
    return api.get(url);
  },
  download: (startDate, endDate, transactionType) => {
    let url = '/passbook/download';
    const params = [];
    // Always include user's timezone for correct time display in PDF
    params.push(`tz=${encodeURIComponent(getUserTimezone())}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (transactionType && transactionType !== 'all') params.push(`transaction_type=${transactionType}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
};

// Buy Page Init API (combined endpoint)
export const buyPageAPI = {
  init: () => api.get('/buy-page-init'),
};

// Gift APIs
export const giftAPI = {
  // Get pending (unacknowledged) gift_received transactions
  getPending: () => api.get('/gift/pending'),
  // Acknowledge a single gift (mark as seen)
  acknowledge: (transactionId) => api.post('/gift/acknowledge', { transaction_id: transactionId }),
  // Acknowledge all pending gifts
  acknowledgeAll: () => api.post('/gift/acknowledge-all'),
  // Check recipient before gifting
  checkRecipient: (phone) => api.post('/gift/check-recipient', { phone }),
  // Get gift history
  getHistory: () => api.get('/gift/history'),
};

// Voucher APIs - Include user's timezone for correct time display in PDFs
export const voucherAPI = {
  downloadReceipt: (transactionId) => api.get(`/voucher/receipt/${transactionId}?tz=${encodeURIComponent(getUserTimezone())}`),
  downloadRefund: (transactionId) => api.get(`/voucher/refund/${transactionId}?tz=${encodeURIComponent(getUserTimezone())}`),
  downloadGold: (voucherId) => api.get(`/voucher/gold/${voucherId}?tz=${encodeURIComponent(getUserTimezone())}`),
};

// User APIs
export const userAPI = {
  updateKYC: (data) => api.put('/user/kyc', data),
  updateBankDetails: (data) => api.put('/user/bank-details', data),
  updateBasicDetails: (data) => api.put('/user/basic-details', data),
  updateProfilePicture: (image_data) => api.put('/user/profile-picture', { image_data }),
  deleteProfilePicture: () => api.delete('/user/profile-picture'),
  // Update name with optional custom token (for registration flow)
  updateName: (name, customToken = null) => {
    const config = customToken ? { headers: { Authorization: `Bearer ${customToken}` } } : {};
    return api.put('/user/name', { name }, config);
  },
  // Get journey stats for sharing
  getJourneyStats: () => api.get('/user/journey-stats'),
  // Delete user account
  deleteAccount: () => api.delete('/user/account'),
};

// Admin APIs
export const adminAPI = {
  checkAccess: () => api.get('/admin/check'),
  getDashboard: () => api.get('/admin/dashboard'),
  getVouchers: () => api.get('/admin/vouchers'),
  updateVoucherStatus: (voucherId, status) => api.put(`/admin/vouchers/${voucherId}`, { status }),
  getUsers: (skip = 0, limit = 50, search = '') => {
    let url = `/admin/users?skip=${skip}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return api.get(url);
  },
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  recalculateUserBalance: (userId) => api.post(`/admin/users/${userId}/recalculate-balance`),
  setUserBalance: (userId, balance) => api.post(`/admin/users/${userId}/set-balance`, { balance }),
  undoRecalculateBalance: (userId) => api.post(`/admin/users/${userId}/undo-recalculate`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  forceDeleteUser: (userId) => api.delete(`/admin/users/${userId}/force`),
  getTransactions: (skip = 0, limit = 50, type = '', status = '', startDate = '', endDate = '') => {
    let url = `/admin/transactions?skip=${skip}&limit=${limit}`;
    if (type) url += `&type=${type}`;
    if (status) url += `&status=${status}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return api.get(url);
  },
  downloadTransactionsCSV: (type = '', status = '', startDate = '', endDate = '') => {
    let url = `/admin/transactions/csv`;
    const params = [];
    if (type) params.push(`type=${type}`);
    if (status) params.push(`status=${status}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  downloadBankRefundCSV: (startDate = '', endDate = '') => {
    let url = `/admin/bank-refund-csv`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  markRefundsProcessed: (startDate = '', endDate = '') => {
    let url = `/admin/mark-refunds-processed`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.post(url);
  },
  updateTransactionStatus: (transactionId, status) => 
    api.put(`/admin/transaction/${transactionId}/status`, { status }),
  processFailedTransaction: (transactionId) => 
    api.post(`/admin/transaction/${transactionId}/process-failed`),
  creditMillisForTransaction: (transactionId) => 
    api.post(`/admin/transaction/${transactionId}/credit-millis`),
  processReferralReward: (userId) => 
    api.post(`/admin/process-referral-reward/${userId}`),
  getDailyReportSettings: () => api.get('/admin/daily-report-settings'),
  saveDailyReportSettings: (emails) => api.post('/admin/daily-report-settings', { emails }),
  sendDailyReportNow: (date = null) => api.post(`/admin/daily-report/send-now${date ? `?date=${date}` : ''}`),
  getSubAdmins: () => api.get('/admin/sub-admins'),
  createSubAdmin: (data) => api.post('/admin/sub-admins', data),
  updateSubAdmin: (phone, data) => api.put(`/admin/sub-admins/${phone}`, data),
  deleteSubAdmin: (phone) => api.delete(`/admin/sub-admins/${phone}`),
  getPermissionsSchema: () => api.get('/admin/permissions/schema'),
  overrideRate: (rate_22kt, reason) => api.post('/admin/override-rate', { rate_22kt, reason }),
  clearRateOverride: () => api.post('/admin/clear-rate-override'),
  deleteAllData: () => api.delete('/admin/delete-all-data'),
  getReferralSettings: () => api.get('/admin/referral-settings'),
  updateReferralSettings: (reward_inr) => api.put('/admin/referral-settings', { reward_inr }),
  
  // Reports
  getProfitLossReport: (startDate, endDate) => api.get(`/admin/reports/profit-loss?start_date=${startDate}&end_date=${endDate}`),
  getOutstandingReport: () => api.get('/admin/reports/outstanding'),
  downloadOutstandingCSV: () => api.get('/admin/reports/outstanding/csv'),
  getDailyReport: (date) => api.get(`/admin/reports/daily?date=${date}`),
  downloadDailyReportCSV: (date, type) => api.get(`/admin/reports/daily/csv?date=${date}&type=${type}`),
  
  // Client Ledger Report
  getClientLedgerReport: (userId = '', startDate = '', endDate = '', excludeFailed = false) => {
    let url = '/admin/reports/client-ledger?';
    const params = [];
    if (userId) params.push(`user_id=${userId}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (excludeFailed) params.push(`exclude_failed=true`);
    return api.get(url + params.join('&'));
  },
  downloadClientLedgerCSV: (userId = '', startDate = '', endDate = '', excludeFailed = false) => {
    let url = '/admin/reports/client-ledger/csv?';
    const params = [];
    if (userId) params.push(`user_id=${userId}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (excludeFailed) params.push(`exclude_failed=true`);
    return api.get(url + params.join('&'));
  },
  
  // Referral Bonus Report
  getReferralBonusReport: (startDate = '', endDate = '') => {
    let url = '/admin/reports/referral-bonus?';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    return api.get(url + params.join('&'));
  },
  downloadReferralBonusCSV: (startDate = '', endDate = '') => {
    let url = '/admin/reports/referral-bonus/csv?';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    return api.get(url + params.join('&'));
  },
  runReferralBonusBackfill: () => api.post('/admin/reports/referral-bonus/backfill'),
  
  // Gold Purchase Tracker Report
  getGoldPurchaseTracker: (startDate = '', endDate = '') => {
    let url = '/admin/reports/gold-purchase-tracker?';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    return api.get(url + params.join('&'));
  },
  downloadGoldPurchaseCSV: (startDate = '', endDate = '') => {
    let url = '/admin/reports/gold-purchase-tracker/csv?';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    return api.get(url + params.join('&'));
  },
  addGoldPurchase: (data) => api.post('/admin/gold-purchase', data),
  deleteGoldPurchase: (purchaseId) => api.delete(`/admin/gold-purchase/${purchaseId}`),
  
  // KYC Identity Management
  getKYCIdentities: (queryString = '') => api.get(`/admin/kyc-identities?${queryString}`),
  getKYCIdentityDetail: (kycIdentityId) => api.get(`/admin/kyc-identities/${kycIdentityId}`),
  restrictKYCIdentity: (kycIdentityId, data) => api.post(`/admin/kyc-identities/${kycIdentityId}/restrict`, data),
  blockKYCIdentity: (kycIdentityId, data) => api.post(`/admin/kyc-identities/${kycIdentityId}/block`, data),
  unblockKYCIdentity: (kycIdentityId) => api.post(`/admin/kyc-identities/${kycIdentityId}/unblock`),
  addKYCNote: (kycIdentityId, data) => api.post(`/admin/kyc-identities/${kycIdentityId}/notes`, data),
  getKYCAlerts: () => api.get('/admin/kyc-identities/alerts/unread'),
  markKYCAlertRead: (alertId) => api.post(`/admin/kyc-identities/alerts/${alertId}/read`),
  runKYCBackfill: () => api.post('/admin/kyc-identities/backfill'),
  // One-active-per-Aadhaar management
  getMultiAccountReport: () => api.get('/admin/kyc-identities/multi-account-report'),
  transferAccount: (kycIdentityId, data) => api.post(`/admin/kyc-identities/${kycIdentityId}/transfer-account`, data),
  
  // PAN Verification Admin
  getPANSubmissions: (params = '') => api.get(`/admin/pan/list?${params}`),
  getPANStats: () => api.get('/admin/pan/stats'),
  getBuyTotalBackfillReport: () => api.get('/admin/pan/backfill-report'),
  getPANPhoto: (userId) => api.get(`/admin/pan/${userId}/photo`),
  approvePAN: (userId) => api.post(`/admin/pan/${userId}/approve`, {}),
  rejectPAN: (userId, reason) => api.post(`/admin/pan/${userId}/reject`, { reason }),
  
  // Generic methods for flexibility
  get: (url) => api.get(`/admin${url}`),
  post: (url, data) => api.post(`/admin${url}`, data),
  deactivateDuplicates: (kycIdentityId) => api.post(`/admin/kyc-identities/${kycIdentityId}/deactivate-duplicates`),
  
  // Employee Referral Codes
  getEmployeeReferrals: (search = '', page = 1) => api.get(`/admin/employee-referrals?search=${search}&page=${page}`),
  addEmployeeReferral: (data) => api.post('/admin/employee-referrals/add', data),
  uploadEmployeeReferralsCsv: (formData) => api.post('/admin/employee-referrals/upload-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getEmployeeReferralsCsvTemplate: () => api.get('/admin/employee-referrals/csv-template'),
  resolveEmployeeReferralCollision: (userId, data) => api.put(`/admin/employee-referrals/${userId}/resolve-collision`, data),
  convertEmployeeToCustomer: (userId) => api.put(`/admin/employee-referrals/${userId}/convert-to-customer`),
  getEmployeeReferralHistory: (userId) => api.get(`/admin/employee-referrals/${userId}/history`),
  
  // Gold Rate Audit
  getGoldRateAudit: (limit = 10) => api.get(`/admin/gold-rate/audit?limit=${limit}`),
  refreshGoldRate: () => api.post('/admin/gold-rate/refresh'),
  getScrapeDiagnostic: () => api.get('/admin/scrape-diagnostic'),
  
  // HDFC Pending Transactions Management
  getHdfcPendingTransactions: (limit = 50) => api.get(`/admin/hdfc/pending-transactions?limit=${limit}`),
  checkHdfcStatus: (transactionId) => api.post(`/admin/hdfc/check-status/${transactionId}`),
  manualCompleteHdfc: (transactionId, bankReference = '', notes = '') => 
    api.post('/admin/hdfc/manual-complete', { transaction_id: transactionId, bank_reference: bankReference, notes }),
  
  // UPI Gateway Pending Transactions Management
  getUpiPendingTransactions: (limit = 50) => api.get(`/upi/admin/pending?limit=${limit}`),
  checkUpiStatus: (orderId) => api.post(`/upi/admin/check-status/${orderId}`),
  manualCompleteUpi: (orderId, bankReference = '', notes = '') => 
    api.post('/upi/admin/manual-complete', { order_id: orderId, bank_reference: bankReference, notes }),
  
  // UPI Payment Corrections (kept for API compatibility)
  getMiscalculatedTransactions: (limit = 100) => api.get(`/upi/admin/miscalculated-transactions?limit=${limit}`),
  previewCorrection: (corrections) => api.post('/upi/admin/preview-correction', { corrections }),
  applyCorrection: (corrections, adminNote = '') => 
    api.post('/upi/admin/apply-correction', { corrections, admin_note: adminNote }),
  getCorrectionAuditLog: (limit = 100) => api.get(`/upi/admin/correction-audit-log?limit=${limit}`),
  
  // Data Audit - Balance Discrepancies
  getBalanceDiscrepancies: () => api.get('/admin/balance-discrepancies'),
  applyBalanceFixes: (userIds) => api.post('/admin/apply-balance-fixes', { user_ids: userIds }),
  
  // Balance Decimal Rounding
  previewBalanceDecimalIssues: () => api.get('/admin/balance-decimal-preview'),
  applyBalanceDecimalFix: () => api.post('/admin/balance-decimal-fix'),
  
  // Admin Balance Adjustments
  getAdjustments: (userId = '', status = '', page = 1, limit = 20) => {
    let url = `/admin/adjustments?page=${page}&limit=${limit}`;
    if (userId) url += `&user_id=${userId}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  getAdjustment: (transactionId) => api.get(`/admin/adjustments/${transactionId}`),
  createAdjustment: (data) => api.post('/admin/adjustments', data),
  editAdjustment: (transactionId, data) => api.put(`/admin/adjustments/${transactionId}`, data),
  voidAdjustment: (transactionId, voidReason) => 
    api.post(`/admin/adjustments/${transactionId}/void`, { void_reason: voidReason }),
  
  // SIP/Autopay Management
  getSIPAnalytics: () => api.get('/admin/sip/analytics'),
  getSIPMandates: (page = 1, limit = 20, status = '', frequency = '', needsReview = null, search = '') => {
    let url = `/admin/sip/mandates?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (frequency) url += `&frequency=${frequency}`;
    if (needsReview !== null) url += `&needs_review=${needsReview}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return api.get(url);
  },
  getSIPMandateDetails: (mandateId) => api.get(`/admin/sip/mandates/${mandateId}`),
  getSIPUserHistory: (userId, includeCancelled = false) => 
    api.get(`/admin/sip/user/${userId}/history?include_cancelled=${includeCancelled}`),
  triggerSIPExecution: (mandateId, reason) => 
    api.post(`/admin/sip/mandates/${mandateId}/execute`, { reason }),
  getSIPAlerts: (page = 1, limit = 20, status = '') => {
    let url = `/admin/sip/alerts?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  resolveSIPAlert: (alertId, resolution, actionTaken) => 
    api.post(`/admin/sip/alerts/${alertId}/resolve`, { resolution, action_taken: actionTaken }),
  getSIPSchedulerStatus: () => api.get('/admin/sip/scheduler/status'),
  triggerSIPSchedulerJob: (jobId) => api.post(`/admin/sip/scheduler/${jobId}/trigger`),
  
};

export default api;
